import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import {
  TrendingUp,
  TrendingDown,
  Clock,
  Users,
  MessageSquare,
  Star,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  RefreshCw
} from 'lucide-react'
import { useChatStore } from '../stores/chatStore'
import { format, subDays, startOfDay, endOfDay, differenceInMinutes, differenceInHours } from 'date-fns'
import api from '../utils/api'

const Analytics = () => {
  const { sessions, messages } = useChatStore()
  const [dateRange, setDateRange] = useState('7d')
  const [isLoading, setIsLoading] = useState(false)
  const [analytics, setAnalytics] = useState({
    totalSessions: 0,
    avgResponseTime: 0,
    satisfactionRate: 0,
    resolutionRate: 0,
    peakHours: [],
    dailyStats: [],
    statusDistribution: {},
    backendAnalytics: null
  })

  useEffect(() => {
    calculateAnalytics()
    fetchBackendAnalytics()
  }, [sessions, dateRange, messages])

  const fetchBackendAnalytics = async () => {
    try {
      setIsLoading(true)
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 1
      const startDate = subDays(new Date(), days).toISOString()
      const endDate = new Date().toISOString()

      const response = await api.get('/chat/analytics', {
        params: { startDate, endDate }
      })

      setAnalytics(prev => ({
        ...prev,
        backendAnalytics: response.data.metrics
      }))
    } catch (error) {
      console.error('Failed to fetch backend analytics:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const calculateAnalytics = () => {
    if (!sessions.length) return

    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 1
    const startDate = startOfDay(subDays(new Date(), days))
    const endDate = endOfDay(new Date())

    const filteredSessions = sessions.filter(session => {
      const sessionDate = new Date(session.createdAt || session.startedAt)
      return sessionDate >= startDate && sessionDate <= endDate
    })

    // Calculate basic metrics
    const totalSessions = filteredSessions.length
    const completedSessions = filteredSessions.filter(s => s.status === 'closed')
    const resolutionRate = totalSessions > 0 ? (completedSessions.length / totalSessions) * 100 : 0

    // Calculate real response times from messages
    const avgResponseTime = calculateAverageResponseTime(filteredSessions)

    // Calculate satisfaction rate from session data
    const satisfactionRate = calculateSatisfactionRate(filteredSessions)

    // Status distribution
    const statusDistribution = filteredSessions.reduce((acc, session) => {
      acc[session.status] = (acc[session.status] || 0) + 1
      return acc
    }, {})

    // Calculate peak hours
    const peakHours = calculatePeakHours(filteredSessions)

    // Daily stats for the chart
    const dailyStats = []
    for (let i = days - 1; i >= 0; i--) {
      const date = subDays(new Date(), i)
      const dayStart = startOfDay(date)
      const dayEnd = endOfDay(date)
      
      const daySessions = filteredSessions.filter(session => {
        const sessionDate = new Date(session.createdAt)
        return sessionDate >= dayStart && sessionDate <= dayEnd
      })

      dailyStats.push({
        date: format(date, 'MMM dd'),
        sessions: daySessions.length,
        completed: daySessions.filter(s => s.status === 'closed').length
      })
    }

    setAnalytics(prev => ({
      ...prev,
      totalSessions,
      avgResponseTime,
      satisfactionRate,
      resolutionRate,
      peakHours,
      dailyStats,
      statusDistribution
    }))
  }

  // Helper function to calculate average response time
  const calculateAverageResponseTime = (sessions) => {
    if (!sessions.length) return 0

    let totalResponseTime = 0
    let responseCount = 0

    sessions.forEach(session => {
      const sessionMessages = messages[session.sessionId] || []
      if (sessionMessages.length < 2) return

      for (let i = 1; i < sessionMessages.length; i++) {
        const currentMsg = sessionMessages[i]
        const prevMsg = sessionMessages[i - 1]

        // Calculate response time between user message and agent/ai response
        if (prevMsg.sender === 'user' && (currentMsg.sender === 'agent' || currentMsg.sender === 'ai')) {
          const responseTime = differenceInMinutes(
            new Date(currentMsg.timestamp || currentMsg.createdAt),
            new Date(prevMsg.timestamp || prevMsg.createdAt)
          )
          if (responseTime >= 0 && responseTime <= 60) { // Filter out unrealistic times
            totalResponseTime += responseTime
            responseCount++
          }
        }
      }

      // Use analytics data if available
      if (session.analytics?.averageResponseTime) {
        totalResponseTime += session.analytics.averageResponseTime / 60000 // Convert ms to minutes
        responseCount++
      }
    })

    return responseCount > 0 ? totalResponseTime / responseCount : 0
  }

  // Helper function to calculate satisfaction rate
  const calculateSatisfactionRate = (sessions) => {
    const sessionsWithSatisfaction = sessions.filter(s => s.satisfaction?.rating)
    if (!sessionsWithSatisfaction.length) return 0

    const totalRating = sessionsWithSatisfaction.reduce((sum, s) => sum + s.satisfaction.rating, 0)
    const avgRating = totalRating / sessionsWithSatisfaction.length

    // Convert 5-star rating to percentage (assuming 4+ stars is satisfied)
    return (avgRating / 5) * 100
  }

  // Helper function to calculate peak hours
  const calculatePeakHours = (sessions) => {
    const hourCounts = {}

    sessions.forEach(session => {
      const hour = new Date(session.createdAt || session.startedAt).getHours()
      hourCounts[hour] = (hourCounts[hour] || 0) + 1
    })

    // Get top 3 peak hours
    return Object.entries(hourCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([hour]) => `${hour.toString().padStart(2, '0')}:00`)
  }

  // Use backend analytics if available, otherwise use calculated analytics
  const backendData = analytics.backendAnalytics
  const displayMetrics = {
    totalSessions: backendData?.totalSessions || analytics.totalSessions,
    avgResponseTime: backendData?.averageResponseTime
      ? (backendData.averageResponseTime / 60000).toFixed(1) // Convert ms to minutes
      : analytics.avgResponseTime.toFixed(1),
    satisfactionRate: backendData?.satisfactionRating
      ? (backendData.satisfactionRating * 20).toFixed(0) // Convert 5-star to percentage
      : Math.round(analytics.satisfactionRate),
    resolutionRate: Math.round(analytics.resolutionRate),
    activeSessions: backendData?.activeSessions || sessions.filter(s => s.status === 'active').length
  }

  const metricCards = [
    {
      name: 'Total Sessions',
      value: displayMetrics.totalSessions,
      change: backendData ? 'Real-time' : 'Calculated',
      changeType: 'neutral',
      icon: MessageSquare,
      color: 'bg-blue-500'
    },
    {
      name: 'Avg Response Time',
      value: `${displayMetrics.avgResponseTime} min`,
      change: displayMetrics.avgResponseTime < 5 ? 'Excellent' : 'Good',
      changeType: displayMetrics.avgResponseTime < 5 ? 'increase' : 'neutral',
      icon: Clock,
      color: 'bg-green-500'
    },
    {
      name: 'Satisfaction Rate',
      value: `${displayMetrics.satisfactionRate}%`,
      change: displayMetrics.satisfactionRate > 80 ? 'Excellent' : 'Good',
      changeType: displayMetrics.satisfactionRate > 80 ? 'increase' : 'neutral',
      icon: Star,
      color: 'bg-yellow-500'
    },
    {
      name: 'Resolution Rate',
      value: `${displayMetrics.resolutionRate}%`,
      change: displayMetrics.resolutionRate > 70 ? 'Good' : 'Needs improvement',
      changeType: displayMetrics.resolutionRate > 70 ? 'increase' : 'decrease',
      icon: TrendingUp,
      color: 'bg-purple-500'
    }
  ]

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600 mt-1">
            Monitor your performance and customer interactions
            {analytics.backendAnalytics && (
              <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                Live Data
              </span>
            )}
          </p>
        </div>

        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="input"
          >
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>

          <button
            onClick={() => {
              calculateAnalytics()
              fetchBackendAnalytics()
            }}
            disabled={isLoading}
            className="btn btn-secondary flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metricCards.map((metric, index) => (
          <motion.div
            key={metric.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                <p className="text-3xl font-bold text-gray-900">{metric.value}</p>
                <div className="flex items-center mt-2">
                  <span
                    className={`text-sm font-medium ${
                      metric.changeType === 'increase'
                        ? 'text-green-600'
                        : metric.changeType === 'decrease'
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {metric.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">
                    {metric.changeType === 'neutral' ? 'Status' : 'vs last period'}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-full ${metric.color}`}>
                <metric.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Session Trends */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Session Trends
            </h3>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-gray-600">Loading analytics...</span>
              </div>
            ) : analytics.dailyStats.length > 0 ? (
              <div className="space-y-4">
                {analytics.dailyStats.map((day, index) => {
                  const maxSessions = Math.max(...analytics.dailyStats.map(d => d.sessions), 1)
                  const percentage = (day.sessions / maxSessions) * 100

                  return (
                    <div key={day.date} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{day.date}</span>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{day.sessions}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No session data available for the selected period</p>
                <p className="text-sm text-gray-400 mt-1">Start some conversations to see trends</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Status Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card"
        >
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <PieChart className="h-5 w-5 mr-2" />
              Session Status
            </h3>
          </div>
          <div className="p-6">
            {Object.keys(analytics.statusDistribution).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(analytics.statusDistribution).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div
                        className={`w-3 h-3 rounded-full mr-3 ${
                          status === 'active' ? 'bg-green-500' :
                          status === 'waiting' ? 'bg-yellow-500' :
                          status === 'closed' ? 'bg-gray-500' : 'bg-blue-500'
                        }`}
                      />
                      <span className="text-sm text-gray-600 capitalize">{status}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <PieChart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No session status data available</p>
                <p className="text-sm text-gray-400 mt-1">Sessions will appear here once created</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Performance Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Activity className="h-5 w-5 mr-2" />
            Performance Insights
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {analytics.peakHours.length || 0}
              </div>
              <div className="text-sm text-gray-600">Peak Hours</div>
              <div className="text-xs text-gray-500 mt-1">
                {analytics.peakHours.length > 0 ? analytics.peakHours.join(', ') : 'No data yet'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {displayMetrics.avgResponseTime}min
              </div>
              <div className="text-sm text-gray-600">Avg Response Time</div>
              <div className="text-xs text-gray-500 mt-1">
                {backendData ? 'From backend data' : 'Calculated from messages'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {displayMetrics.activeSessions}
              </div>
              <div className="text-sm text-gray-600">Active Sessions</div>
              <div className="text-xs text-gray-500 mt-1">
                Currently ongoing
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default Analytics
